// /**
//  * Adapters: convert API DTOs → mock-compatible shapes used by public components.
//  * This keeps public components untouched while the API becomes the source of truth.
//  */


import type { ArticleDto } from "@/types/article.types";
import type { VerificationDto } from "@/types/verification.types";
import type { Article, FactCheck, Verdict, LangCode, LiveEvent, LiveUpdate, Priority, Multilingual } from "@/lib/mock-data";
import type { EvenementDirectDto, MiseAJourDirectDto } from "@/types/evenements-direct.types";

// adapters.ts — correction du mapping des langues
export function adaptArticle(dto: ArticleDto): Article {
  const title: Record<string, string> = {};
  const subtitle: Record<string, string> = {};
  const summary: Record<string, string> = {};

  for (const t of dto.traductions ?? []) {
    const lang = t.langue?.toLowerCase(); // ✅ normaliser en minuscules
    if (!lang) continue;
    if (t.titre) title[lang] = t.titre;
    if (t.sousTitre) {
      subtitle[lang] = t.sousTitre;
      // Pas de champ "résumé" distinct côté API : le chapô (sousTitre) en tient
      // lieu. Sans cette ligne, `summary` restait toujours vide (déclaré mais
      // jamais rempli), donc silencieusement absent partout où il est utilisé
      // (aperçu chapô sur les cartes, lecture audio qui sautait le chapô).
      summary[lang] = t.sousTitre;
    }
  }

  const imageUrl = dto.medias?.find((m) => m.type === "image")?.url;
  const videoUrl = dto.medias?.find((m) => m.type === "video")?.url;
  const audioUrl = dto.medias?.find((m) => m.type === "audio")?.url;
  const coverImageUrl = dto.imageCouverture ?? imageUrl;
  
  const byline = dto.signature === "REDACTION_PARTENAIRE"
    ? "partner-desk"
    : "desk";

  const categories = (dto.categorieIds ?? []).map(String);
  const pays = (dto.paysIds ?? []).map(String);

  // ✅ Normaliser aussi les langues disponibles
  const availableLanguages = (dto.languesDisponibles ?? []).map((l) =>
    l.toLowerCase()
  ) as LangCode[];

  // ✅ Gérer les deux formes que l'API renvoie (aaudio / aAudio)
  const hasAudio = dto.aAudio ?? dto.aaudio ?? false;
  const hasAvatarVideo = dto.aVideoAvatar ?? dto.avideoAvatar ?? false;

  return {
    id: String(dto.id),
    slug: dto.slug,
    title: title as any,
    subtitle: Object.keys(subtitle).length ? subtitle as any : undefined,
    summary: Object.keys(summary).length ? summary as any : undefined,
    categories,
    pays,
    tags: dto.etiquettes?.map((e) => e.slug) ?? [],
    type: (dto.type?.toLowerCase() as any) ?? "article",
    publishedAt: dto.publieLe ?? new Date().toISOString(),
    updatedAt: dto.modifieLe,
    readingTime: dto.tempsLecture ?? 3,
    availableLanguages,
    hasAudio,
    hasAvatarVideo,
    isCorrected: dto.estCorrige,
    byline,
    imageUrl,
    videoUrl,
    audioUrl,
    coverImageUrl,
    medias: dto.medias ?? [],
  };
}

// ✅ Même correction pour adaptVerification
export function adaptVerification(dto: VerificationDto): FactCheck {
  const claim: Record<string, string> = {};
  const shortExplanation: Record<string, string> = {};
  const whatIsCirculating: Record<string, string> = {};
  const whatWeVerified: Record<string, string> = {};
  const evidence: Record<string, string> = {};
  const whyItMatters: Record<string, string> = {};
  const safeShare: Record<string, string> = {};

  for (const t of dto.traductions ?? []) {
    const lang = t.langue?.toLowerCase(); // ✅ normaliser
    if (!lang) continue;
    if (t.affirmation) claim[lang] = t.affirmation;
    if (t.explicationCourte) shortExplanation[lang] = t.explicationCourte;
    if (t.ceQuiCircule) whatIsCirculating[lang] = t.ceQuiCircule;
    if (t.ceQueNousAvonsVerifie) whatWeVerified[lang] = t.ceQueNousAvonsVerifie;
    if (t.preuves) evidence[lang] = t.preuves;
    if (t.pourquoiImportant) whyItMatters[lang] = t.pourquoiImportant;
    if (t.partageSecurise) safeShare[lang] = t.partageSecurise;
  }

  // ✅ NON_PROUVE manquait dans le map
  const VERDICT_MAP: Record<string, Verdict> = {
    FAUX: "false",
    VRAI: "true",
    TROMPEUR: "misleading",
    NON_VERIFIE: "unproven",
    NON_PROUVE: "unproven", // ✅ ajout
    CONTEXTE: "context",
  };

  return {
    id: String(dto.id),
    slug: dto.slug,
    claim: claim as any,
    verdict: VERDICT_MAP[dto.verdict] ?? "unproven",
    shortExplanation: shortExplanation as any,
    whatIsCirculating: Object.keys(whatIsCirculating).length ? whatIsCirculating as any : undefined,
    whatWeVerified: Object.keys(whatWeVerified).length ? whatWeVerified as any : undefined,
    evidence: Object.keys(evidence).length ? evidence as any : undefined,
    whyItMatters: Object.keys(whyItMatters).length ? whyItMatters as any : undefined,
    safeShare: Object.keys(safeShare).length ? safeShare as any : undefined,
    date: dto.date,
    category: String(dto.categorieId ?? "factcheck"),
    format: (dto.format?.toLowerCase() as any) ?? "text",
    attachments: dto.attachments ?? [],
  };

  
}

export function adaptArticles(dtos: ArticleDto[]): Article[] {
  return dtos.map(adaptArticle);
}

export function adaptVerifications(dtos: VerificationDto[]): FactCheck[] {
  return dtos.map(adaptVerification);
}

// ─── Live / Direct ────────────────────────────────────────────────────────────

function normaliseLangueMap(map: Record<string, string> | undefined): Multilingual {
  const result: Multilingual = {};
  for (const [k, v] of Object.entries(map ?? {})) {
    (result as Record<string, string>)[k.toLowerCase()] = v;
  }
  return result;
}

const PRIORITE_MAP: Record<string, Priority> = {
  CONFIRME: "confirmed",
  EN_DEVELOPPEMENT: "developing",
  CORRECTION: "correction",
  ALERTE: "alert",
};

export function adaptPriorite(p: string): Priority {
  return PRIORITE_MAP[p] ?? "developing";
}

export function adaptMiseAJourDirect(dto: MiseAJourDirectDto, liveId: string): LiveUpdate {
  return {
    id: String(dto.id),
    liveId,
    timestamp: dto.horodatage,
    priority: adaptPriorite(dto.priorite),
    text: normaliseLangueMap(dto.texte),
    pinned: dto.epinglee,
  };
}

export function adaptEvenementDirect(dto: EvenementDirectDto): LiveEvent {
  return {
    id: String(dto.id),
    title: normaliseLangueMap(dto.titre),
    startedAt: dto.demarreLe,
    closed: !!dto.fermeLe,
    updates: (dto.misesAJour ?? []).map((m) => adaptMiseAJourDirect(m, String(dto.id))),
  };
}